import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { ThemeContext } from '../../context/ThemeContext';
import { API_BASE } from '../../api';

function PendingTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);


  // 🎨 GLOBAL COLORS DEFINITION (Based on Dashboard.css)
  const colors = {
    // Dashboard.css Background/Elements
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',

    // Primary Colors and Gradients used in Dashboard.css
    primary: currentTheme?.primary || '#6366F1', // Indigo/Violet (used for orb-1, badge-role, chart-assigned)
    secondary: currentTheme?.secondary || '#8B5CF6', // Purple/Pink (used for orb-2)
    accent: currentTheme?.accent || '#06B6D4', // Cyan (used for orb-3)
    success: '#10B981', // Green (used for orb-4, completion-rate, chart-completed)
    warning: '#F59E0B', // Orange (used for chart-unassigned)
    danger: '#EF4444', // Red (used for error-alert background)

    // Specific gradients simplified to match the colors above
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Blue/Violet (close to primary/secondary)
    gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
    gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
    gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
  };

  // Styles matching the consistent design
  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none'
    },
    floatingOrb: {
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(40px)',
      opacity: 0.3,
      animation: 'float 8s ease-in-out infinite'
    },
    orb1: {
      width: '300px',
      height: '300px',
      background: colors.primary,
      top: '10%',
      left: '5%',
      animationDelay: '0s'
    },
    orb2: {
      width: '200px',
      height: '200px',
      background: colors.secondary,
      top: '60%',
      right: '10%',
      animationDelay: '2s'
    },
    orb3: {
      width: '250px',
      height: '250px',
      background: colors.accent,
      bottom: '20%',
      left: '15%',
      animationDelay: '4s'
    },
    orb4: {
      width: '180px',
      height: '180px',
      background: colors.success,
      top: '30%',
      right: '20%',
      animationDelay: '6s'
    },
    gridOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                         linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: {
      padding: '40px 40px 30px',
      position: 'relative',
      zIndex: 2
    },
    headerContent: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    greeting: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '15px'
    },
    pageTitle: {
      fontSize: '3rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      margin: 0
    },
    welcomeBadge: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '8px 16px',
      borderRadius: '20px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    badgeText: {
      color: 'white',
      fontWeight: '600',
      fontSize: '0.9rem'
    },
    badgeRole: {
      background: colors.primary,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    pageSubtitle: {
      color: colors.textLight,
      fontSize: '1.2rem',
      margin: 0,
      maxWidth: '600px'
    },
    messageAlert: {
      color: 'white',
      padding: '20px',
      borderRadius: '15px',
      margin: '0 40px 30px',
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    alertIcon: {
      fontSize: '1.5rem'
    },
    alertMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      color: 'white'
    },
    alertClose: {
      background: 'none',
      border: 'none',
      color: colors.danger,
      cursor: 'pointer',
      padding: '8px',
      borderRadius: '8px',
      transition: 'background 0.3s ease'
    },
    content: {
      padding: '0 40px 40px',
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 2
    },
    formCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '25px',
      border: `1px solid ${colors.borderDark}`,
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
    },
    cardHeader: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      padding: '25px 30px'
    },
    cardTitle: {
      fontSize: '1.8rem',
      fontWeight: '700',
      margin: 0
    },
    cardBody: {
      padding: '40px'
    },
    backBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none',
      marginBottom: '20px'
    },
    loadingState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '100px 40px',
      color: 'white',
      textAlign: 'center'
    },
    loadingText: {
      fontSize: '2rem',
      margin: '0 0 20px 0',
      fontWeight: '600',
      background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    loadingSubtext: {
      color: colors.textLight,
      margin: 0,
      fontSize: '1.2rem'
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    tableHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      borderBottom: `2px solid ${colors.borderDark}`,
      padding: '15px 12px',
      fontWeight: '600'
    },
    tableRow: {
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
      color: 'white'
    },
    tableCell: {
      padding: '12px',
      verticalAlign: 'middle'
    },
    sectionHeader: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: colors.primary,
      fontWeight: 'bold',
      fontSize: '1.1rem'
    },
    actionBtn: {
      background: 'rgba(255, 255, 255, 0.1)',
      color: 'white',
      border: `1px solid ${colors.borderDark}`,
      padding: '10px 20px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      textDecoration: 'none'
    },
    primaryBtn: {
      background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }
  };

  useEffect(() => {
    fetchPendingTasks();
  }, []);

  const fetchPendingTasks = async () => {
    try {
      setLoading(true);
      setMessage('');
      
      const editorId = sessionStorage.getItem('userId');
      
      if (!editorId) {
        throw new Error('Editor ID not found. Please login again.');
      }

      console.log('Fetching pending tasks for editor:', editorId);
      
      const response = await fetch(`${API_BASE}/get_editor_tasks.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'get_editor_tasks',
          editor_id: parseInt(editorId)
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed data:', data);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Invalid JSON response from server');
      }

      // Handle different response formats and add edited_status
      let processedTasks = [];
      if (data && Array.isArray(data)) {
        processedTasks = data.map(task => ({
          ...task,
          edited_status: task.edited_video_link ? 'completed' : 'pending'
        }));
      } else if (data && data.tasks) {
        processedTasks = data.tasks.map(task => ({
          ...task,
          edited_status: task.edited_video_link ? 'completed' : 'pending'
        }));
      } else if (data && data.data && data.data.tasks) {
        processedTasks = data.data.tasks.map(task => ({
          ...task,
          edited_status: task.edited_video_link ? 'completed' : 'pending'
        }));
      } else {
        throw new Error('Invalid response format from server');
      }

      processTasksArray(processedTasks);
      
    } catch (error) {
      console.error('❌ Error fetching pending tasks:', error);
      setMessage('❌ ' + error.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const processTasksArray = (tasksArray) => {
    if (!Array.isArray(tasksArray)) {
      throw new Error('Expected array of tasks');
    }

    // Filter pending tasks
    const pendingTasks = tasksArray.filter(task => 
      task.status === 'pending' || 
      task.status === 'assigned' || 
      task.status === 'in_progress' ||
      (task.display_status && (
        task.display_status.toLowerCase().includes('pending') ||
        task.display_status.toLowerCase().includes('assigned') ||
        task.display_status.toLowerCase().includes('progress') ||
        task.display_status.toLowerCase().includes('due')
      ))
    );

    console.log('Found pending tasks:', pendingTasks.length);
    setTasks(pendingTasks);
    
    if (pendingTasks.length === 0) {
      setMessage('ℹ️ No pending tasks found. All tasks are completed or not assigned to you.');
    }
  };

  const handleWorkOnTask = (task) => {
    navigate('/submit-video', { state: { task } });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge ${config.class} fs-6`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getEditedStatusBadge = (editedStatus) => {
    const editedStatusConfig = {
      completed: { class: 'bg-success text-white', text: 'Edited', icon: 'fa-check-circle' },
      pending: { class: 'bg-secondary text-white', text: 'Not Edited', icon: 'fa-pen' }
    };
    
    const config = editedStatusConfig[editedStatus] || editedStatusConfig.pending;
    return (
      <span className={`badge ${config.class} fs-6`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getActionStatus = (task) => {
    const status = task.status;
    const editedStatus = task.edited_status;
    
    // If both status and edited status are completed, show "Complete"
    if ((status === 'completed' || status === 'approved') && editedStatus === 'completed') {
      return 'Complete';
    }
    
    // Otherwise show "Open"
    return 'Open';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.container}>
          <div className="animated-background" style={styles.animatedBackground}>
            <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
            <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
            <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
            <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
            <div className="grid-overlay" style={styles.gridOverlay}></div>
          </div>
          <div style={styles.loadingState}>
            <div className="loading-spinner" style={styles.spinner}></div>
            <h3 style={styles.loadingText}>Loading Pending Tasks</h3>
            <p style={styles.loadingSubtext}>Please wait while we fetch your pending tasks</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Animated Background */}
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        {/* Header Section */}
        <div className="pending-tasks-header" style={styles.header}>
          <div className="header-content" style={styles.headerContent}>
            <div className="welcome-section">
              <div className="greeting" style={styles.greeting}>
                <h1 className="page-title" style={styles.pageTitle}>
                  <i className="fas fa-clock me-2"></i>
                  Pending Tasks
                </h1>
                <div className="welcome-badge" style={styles.welcomeBadge}>
                  <span className="badge-text" style={styles.badgeText}>Task Management</span>
                  <span className="badge-role" style={styles.badgeRole}>Pending</span>
                </div>
              </div>
              <p className="page-subtitle" style={styles.pageSubtitle}>
                Tasks that are pending or in progress
                {tasks.length > 0 && (
                  <span className="ms-2 badge" style={{background: colors.primary, color: 'white'}}>
                    {tasks.length} tasks
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className="message-alert" style={{
            ...styles.messageAlert,
            background: message.includes('✅') 
              ? 'rgba(16, 185, 129, 0.1)' 
              : message.includes('ℹ️') 
              ? 'rgba(13, 110, 253, 0.1)'
              : 'rgba(220, 53, 69, 0.1)',
            border: message.includes('✅') 
              ? `1px solid ${colors.success}`
              : message.includes('ℹ️') 
              ? `1px solid ${colors.primary}`
              : `1px solid ${colors.danger}`,
            color: message.includes('✅') 
              ? colors.success
              : message.includes('ℹ️') 
              ? colors.primary
              : colors.danger
          }}>
            <div className="alert-icon" style={{...styles.alertIcon, 
              color: message.includes('✅') 
                ? colors.success
                : message.includes('ℹ️') 
                ? colors.primary
                : colors.danger
            }}>
              <i className={`fas ${
                message.includes('✅') ? 'fa-check-circle' : 
                message.includes('ℹ️') ? 'fa-info-circle' :
                'fa-times-circle'
              }`}></i>
            </div>
            <div className="alert-message" style={styles.alertMessage}>
              <strong>
                {message.includes('✅') ? 'Success' : 
                 message.includes('ℹ️') ? 'Info' :
                 'Error'}
              </strong>
              <span style={{color: 'white'}}>{message}</span>
            </div>
            <button className="alert-close" onClick={() => setMessage('')} style={styles.alertClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="pending-tasks-content" style={styles.content}>
          <div style={{display: 'flex', gap: '15px', marginBottom: '20px'}}>
            <button 
              style={styles.actionBtn}
              onClick={fetchPendingTasks}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
              Refresh
            </button>
          </div>

          {/* Tasks Card */}
          <div className="tasks-card" style={styles.formCard}>
            <div className="card-header" style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>
                <i className="fas fa-list me-2"></i>
                Pending Task List
              </h3>
            </div>
            <div style={{padding: '0'}}>
              {tasks.length === 0 ? (
                <div style={{padding: '60px 40px', textAlign: 'center'}}>
                  <i className="fas fa-clock fa-4x mb-3" style={{color: `${colors.textLight}20`}}></i>
                  <h4 style={{color: colors.textLight, marginBottom: '15px'}}>No pending tasks</h4>
                  <p style={{color: colors.textLight, marginBottom: '25px'}}>
                    You don't have any pending tasks at the moment.
                  </p>
                  <button 
                    style={styles.primaryBtn}
                    onClick={fetchPendingTasks}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    Refresh
                  </button>
                </div>
              ) : (
                <div style={{overflowX: 'auto'}}>
                  <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse'}}>
                    <thead>
                      <tr>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-film me-2"></i>
                          Video Name
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-user-tie me-2"></i>
                          Client
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-user me-2"></i>
                          Operator
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-flag me-2"></i>
                          Status
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-edit me-2"></i>
                          Edited Status
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-tasks me-2"></i>
                          Action Status
                        </th>
                        <th style={styles.tableHeader}>
                          <i className="fas fa-play me-2"></i>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr key={task.id} style={styles.tableRow}>
                          <td style={styles.tableCell}>
                            <div>
                              <strong className="d-block" style={{color: 'white'}}>
                                <i className="fas fa-video me-2" style={{color: colors.accent}}></i>
                                {task.video_name || 'Unnamed Video'}
                              </strong>
                              {task.notes && (
                                <small style={{ color: colors.textLight, marginTop: '8px', display: 'block' }}>
                                  <i className="fas fa-sticky-note me-1"></i>
                                  {task.notes.substring(0, 50)}...
                                </small>
                              )}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <span style={{color: 'white', fontWeight: '600'}}>
                              <i className="fas fa-user-tie me-2" style={{color: colors.textLight}}></i>
                              {task.client_name || 'N/A'}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <span style={{color: 'white', fontWeight: '600'}}>
                              <i className="fas fa-user me-2" style={{color: colors.textLight}}></i>
                              {task.operator_name || 'N/A'}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            {getStatusBadge(task.status)}
                          </td>
                          <td style={styles.tableCell}>
                            {getEditedStatusBadge(task.edited_status)}
                          </td>
                          <td style={styles.tableCell}>
                            <span className={`badge fs-6 ${
                              getActionStatus(task) === 'Complete' 
                                ? 'bg-success text-white' 
                                : 'bg-warning text-dark'
                            }`}>
                              <i className={`fas ${
                                getActionStatus(task) === 'Complete' 
                                  ? 'fa-check-double' 
                                  : 'fa-lock-open'
                              } me-1`}></i>
                              {getActionStatus(task)}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <button
                              style={{
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}
                              onClick={() => handleWorkOnTask(task)}
                              title="Start working on this task"
                            >
                              <i className="fas fa-edit me-1"></i>
                              Work on Task
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .spinning {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        @keyframes wave {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          } 40% {
            transform: scale(1);
          }
        }
        
        /* Custom Hover Effects */
        .form-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
        }

        .table {
          --bs-table-bg: transparent;
          --bs-table-color: white;
        }

        .table-hover tbody tr:hover {
          background: rgba(255, 255, 255, 0.05) !important;
        }

        .btn-link:hover {
          opacity: 0.8;
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
}

export default PendingTasks;